/**
 * One task on the project's list, plus the head that names its columns.
 *
 * Split out of `ProjectTasks` when the assignee column and the milestone
 * grouping landed: that file is now the QUERY (what is asked for, how the answer
 * is grouped) and this one is the RECORD it draws. The head lives here rather
 * than beside the query because head and row share `TASK_COLS` - a column added
 * to one and not the other slides every cell after it one track sideways, and
 * that is a bug no diff shows if the two halves sit in different files.
 *
 * WHO IS ON IT is a column, unlike the `visibleToClient` mark beside the title,
 * and the difference is worth writing down because from a distance the two look
 * like the same kind of fact. The eye is drawn only for the exception because
 * "the client can see this" is true on nearly every row: a column would spend a
 * track repeating it and teach the eye to skip that track. An assignee is not a
 * flag with a normal value - it is an identity. It differs from row to row the
 * moment anybody fills it in, and there is no glyph that can stand in for "who".
 * So it gets a track of its own: fixed and narrow, because faces need far less
 * width than the names behind them, and the four columns to the right are the
 * ones that must not be squeezed.
 *
 * That track is EMPTY on every row imported from the old CRM. The importer found
 * a single host user and refused to invent owners, so all 112 production tasks
 * name nobody. A column of dashes is the correct picture of that, and the dash
 * is `NOBODY_MARK` - the shared mark for "no id was ever stored", the same one
 * the members strip and the time sheet use - so the reading is "nobody has been
 * named" rather than "this cell failed". The hover says it in words, which is
 * the one thing `UserAvatars` cannot do for itself: it draws the mark but has no
 * business choosing the wording for every screen that borrows it.
 */
import { Link } from "react-router-dom";
import { CalendarClock, Check, CheckSquare, EyeOff, MoreHorizontal } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Task, TaskStatus } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { StatusPill, fmtDate, fmtNum } from "../../ui";
import { UserAvatars } from "../_shared/UserChips";
import { TimerMark, TimerMenuItem } from "../../timer";
import { isOverdue } from "./lib";
import { TASK_STATUSES, taskTone } from "./status";

/**
 * Title, assignees, status, due date, estimate, billable, actions.
 *
 * The assignee track is `px` rather than `fr` on purpose. Every flexible track
 * raises the width the card refuses to draw a list below (`listFloor` counts
 * 130px per `fr`), and a stack of three 26px discs is a hundred pixels wide
 * whatever the window does - so a seventh flexible column would have cost the
 * list 144px of minimum width to hold something that never needs it.
 */
export const TASK_COLS = "minmax(0,2.4fr) 96px 1fr .9fr .6fr .8fr 44px";

/** How many faces before the "+N" tail takes over, and how big each one is.
 *  Three plus the tail is 74px, which is what the 96px track was chosen for. */
const FACES = 3;
const FACE_SIZE = 26;

export function ProjectTaskHead() {
  const t = useT();
  return (
    <div className="crm-list-head" style={{ gridTemplateColumns: TASK_COLS, paddingTop: 22 }}>
      <span>{t("tasks.titleField")}</span>
      <span>{t("projects.assignees")}</span>
      <span>{t("projects.statusCol")}</span>
      <span>{t("tasks.dueDate")}</span>
      <span>{t("tasks.estimateHours")}</span>
      <span>{t("tasks.billable")}</span>
      <span />
    </div>
  );
}

/**
 * The row's menu: the clock first, then where the task stands.
 *
 * It takes the task's id purely for the timer. This tab has its own row and its
 * own menu rather than sharing the register's `TaskRowMenu`, so a control added
 * there does NOT appear here - and a project page whose task list is the only
 * one without a start button is exactly where somebody working from a project
 * would go looking for it.
 */
function StatusMenu({ taskId, value, onPick }: { taskId: string; value: TaskStatus; onPick: (s: TaskStatus) => void }) {
  const t = useT();
  const close = useClosePops();
  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <TimerMenuItem taskId={taskId} onDone={close} />
        <div className="crm-menu-sep" />

        {TASK_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={cn("crm-mi", s === value && "is-on")}
            onClick={() => {
              onPick(s);
              close();
            }}
          >
            <span className="flex-1">{t(`tasks.status.${s}`)}</span>
            {s === value ? <Check className="size-4" /> : null}
          </button>
        ))}
      </Pop>
    </span>
  );
}

export function ProjectTaskRow({ task, onStatus }: { task: Task; onStatus: (s: TaskStatus) => void }) {
  const t = useT();
  // The same reading `TasksListRow` writes down: undefined means "this row did
  // not come from the LIST endpoint and nobody counted the boxes here", while
  // `{ done: 0, total: 0 }` is a real answer - the task has no checklist. This
  // tab does call the list endpoint (`getTasks` in `ProjectTasks.tsx`, which is
  // where `withChecklistProgress` runs), so the figure is here on every row; the
  // distinction still matters, because neither shape is a reason to draw a
  // placeholder and "0/0" is not progress.
  const checklist = task.checklistProgress;

  return (
    <div className="crm-list-row relative" style={{ gridTemplateColumns: TASK_COLS }}>
      {/* The title cell, and beside it the one thing about this row a reader
          cannot infer from the columns to its right. */}
      <span className="flex min-w-0 items-center gap-1.5 pl-3">
        <Link
          to={`/tasks/${task.id}`}
          className={cn(
            "crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full",
            task.status === "done" && "crm-mut line-through",
          )}
        >
          {task.title}
        </Link>

        {/* A closed eye, not a lock: this work is not restricted, it is simply
            not shown to the person paying for it. The wrapper carries the
            tooltip because an `<svg>` ignores a `title` attribute (SVG wants a
            `<title>` child), and it sits above the link's row-wide overlay for
            the same reason `StatusMenu` does - a tooltip under the overlay never
            opens. The cost is a 14px patch of the row that does not navigate,
            which is the cheaper half of the trade: an unexplained glyph is worse
            than a dead pixel. */}
        {/* A clock running on this task, anybody's - the same mark the task
            register draws, so the two lists say the same thing about the same
            row. */}
        <TimerMark taskId={task.id} />

        {task.visibleToClient ? null : (
          <span
            className="crm-mut relative z-[1] inline-flex shrink-0"
            role="img"
            title={t("projects.hiddenFromClient")}
            aria-label={t("projects.hiddenFromClient")}
          >
            <EyeOff className="size-3.5" />
          </span>
        )}

        {/* How much of the PLAN for this task is ticked - the same pair of quiet
            numbers the register row and the task board draw, so one badge means
            one thing wherever a task is listed. It says nothing about the task
            itself: a full checklist does not finish a task, which is why this is
            two numbers and not anything the eye could read as a state.

            Only where there are boxes. "0/0" on every task nobody has broken up
            would put a figure about nothing on most rows, and it is not the same
            statement as a missing count anyway.

            `crm-mut` and a size, which the register's copy does not carry: there
            the badge sits on the row's small second line and inherits both. This
            row has no second line, so the badge rides the title line and has to
            step back from the title itself deliberately. `relative z-[1]` for
            the reason the eye above it gives - the title link covers the row
            with an ::after, and a tooltip under that overlay never opens, which
            would leave two numbers and a slash that could be anything. */}
        {checklist && checklist.total > 0 ? (
          <span
            className="crm-mut crm-tnum relative z-[1] flex shrink-0 items-center gap-1 text-[12.5px]"
            title={t("tasks.checklist")}
          >
            <CheckSquare className="size-3 shrink-0" />
            {checklist.done}/{checklist.total}
          </span>
        ) : null}
      </span>

      {/* The wrapper's tooltip is set only when there is nobody to name: with
          faces in the cell `UserAvatars` writes its own, listing them. */}
      <span
        className="flex min-w-0 items-center"
        title={task.assignees.length ? undefined : t("projects.unassigned")}
      >
        <UserAvatars ids={task.assignees} max={FACES} size={FACE_SIZE} />
      </span>

      <span>
        <StatusPill tone={taskTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusPill>
      </span>

      <span
        className={cn(
          "crm-tnum flex items-center gap-1.5 text-[12.5px]",
          isOverdue(task) ? "text-red-600" : "crm-mut",
        )}
      >
        <CalendarClock className="size-3.5 shrink-0" />
        {fmtDate(task.dueDate)}
      </span>

      {/* An estimate nobody entered is a dash, not a zero: "0 h" is a promise
          that this takes no work. */}
      <span className="crm-mut crm-tnum text-[12.5px]">
        {task.estimateHours == null ? "-" : `${fmtNum(task.estimateHours)} h`}
      </span>

      {/* The same pill the time tab draws, so one word means one thing across
          the project: this work can be charged for. */}
      <span>
        <StatusPill tone={task.billable ? "info" : "mut"}>
          {t(task.billable ? "projects.billable" : "projects.nonBillable")}
        </StatusPill>
      </span>

      <StatusMenu taskId={task.id} value={task.status} onPick={onStatus} />
    </div>
  );
}
